import React, { memo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNotch,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

import BrandingContext from '../../Context/BrandingContext';
import IconsContext from '../../Context/IconsContext';
import OrganizationService from '../../Services/OrganizationService';
import RootOrgContext from '../../Context/RootOrgContext';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import ModalLayout from '../ModalLayout';
import { BrandPrimarySpinningCircle } from '../LoadingCircle';

export default memo(AddDashboardUsersModal);

const initState = {
  dashboardUsers: [],
  ecosystemUsers: [],
  error: '',
  requestPending: true,
  saveRequestPending: false,
};

const selectStyles = {
  control: (base) => ({
    ...base,
    borderColor: 'var(--third-gray) !important',
    outline: 'none',
    boxShadow: 'none',
  }),
  dropdownIndicator: (base) => ({ ...base, display: 'none' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
};

function AddDashboardUsersModal(props) {
  const { isSupraUi, isVersatileUi } = useContext(BrandingContext);
  const { profileIcon } = useContext(IconsContext);
  const [state, updateState] = useState(initState);

  const source = CancelToken.source();
  const { _id: rootOrgID } = useContext(RootOrgContext);
  const { _id: currentOrgID, path: currentOrgPath } =
    useContext(CurrentOrgContext);
  const pathOrgIDs = currentOrgPath.split('.');

  const disabledOptions = ({ value: { userID } }) => {
    return state.dashboardUsers.some((u) => u.userID === userID);
  };

  const userIsInPath = (Orgs) => {
    if (!Array.isArray(Orgs)) return false;

    return Orgs.some(({ _id, OrgUser }) => {
      switch (true) {
        case _id === currentOrgID:
        case pathOrgIDs.includes(OrgUser.orgID):
          return true;
        case !Object.keys(OrgUser || {}).length:
          return false;
        default:
          return false;
      }
    });
  };

  const fetchEcosystemUsers = async () => {
    try {
      const allUsers =
        await new OrganizationService().fetchAllOrganizationUsers(
          rootOrgID,
          source.token
        );

      const { dashboardUsers, ecosystemUsers } = allUsers.reduce(
        (acc, u) => {
          const { _id: userID, email, firstName, lastName, Orgs } = u;
          if (!userIsInPath(Orgs)) return acc;

          const value = { userID, email, firstName, lastName };

          if (props.dashboard.users.includes(userID)) {
            acc.dashboardUsers.push(value);
          }

          acc.ecosystemUsers.push({
            label: `${firstName} ${lastName} (${email})`,
            value,
          });
          return acc;
        },
        { dashboardUsers: [], ecosystemUsers: [] }
      );

      updateState({
        ...state,
        dashboardUsers,
        ecosystemUsers,
        requestPending: false,
      });
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, requestPending: false });
    }
  };

  const filterOptions = (
    { value: { email = '', firstName = '', lastName = '' } },
    search
  ) => {
    if (!search) return true;

    const prunedEmail = email.toLowerCase().trim();
    const prunedFirst = firstName.toLowerCase().trim();
    const prunedLast = lastName.toLowerCase().trim();
    const prunedSearch = search.toLowerCase().trim();
    return (
      prunedEmail.includes(prunedSearch) ||
      prunedFirst.includes(prunedSearch) ||
      prunedLast.includes(prunedSearch)
    );
  };

  const handleAddUser = () => {
    const dashboardUsers = [state.selectedUser.value, ...state.dashboardUsers];
    updateState({ ...state, dashboardUsers, selectedUser: null });
  };

  const handleRemoveUser = ({ userID }) => {
    const dashboardUsers = state.dashboardUsers.filter((u) => {
      return u.userID !== userID;
    });
    updateState({ ...state, dashboardUsers });
  };

  const handleUserSelect = (selectedUser) => {
    updateState({ ...state, selectedUser });
  };

  const handleSave = async () => {
    try {
      updateState({ ...state, saveRequestPending: true });
      const users = state.dashboardUsers.map(({ userID }) => userID);
      await props.handleAddUsersSave(users);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, saveRequestPending: false });
    }
  };

  useEffect(() => {
    fetchEcosystemUsers();
    return source.cancel;
  }, []);

  return (
    <ModalLayout isOpen={props.isOpen} toggleModal={props.toggleModal}>
      <ModalHeader className="border-bottom-0 pb-0" toggle={props.toggleModal}>
        Manage Users For Private Dashboard
      </ModalHeader>
      <ModalBody className="d-flex flex-column px-md-5">
        <p className="modal-supplemental-text w-75">
          Manage users from the ecosystem with to access this private dashboard.
          Anyone added to the dashboard will be able to add others.
        </p>
        <div className="d-flex">
          <div className="flex-grow-1 mr-3">
            <Select
              filterOption={filterOptions}
              isClearable
              isLoading={state.requestPending}
              isOptionDisabled={disabledOptions}
              onChange={handleUserSelect}
              options={state.ecosystemUsers}
              placeholder="Give user access..."
              styles={selectStyles}
              value={state.selectedUser}
            />
          </div>
          <div className="flex-shrink-1">
            <button
              className="btn btn-block h-100 modal-button-secondary three-quarter-text rounded-lg px-3"
              disabled={!state.selectedUser}
              onClick={handleAddUser}
            >
              Add
            </button>
          </div>
        </div>
        <div className="mt-3 flex-fill position-relative overflow-auto">
          {state.requestPending ? (
            <div className="h-100 w-100 d-flex align-items-center justify-content-center">
              <BrandPrimarySpinningCircle size="3x" />
            </div>
          ) : (
            <div className="h-100 w-100 position-absolute container-fluid">
              {state.dashboardUsers.map((u) => (
                <div className="row align-items-center my-2" key={u.userID}>
                  <div className="col-auto">
                    {isSupraUi || isVersatileUi ? (
                      <FontAwesomeIcon icon={faUser} size="lg" />
                    ) : (
                      <img
                        alt="profile icon"
                        height={30}
                        src={profileIcon}
                        width={30}
                      />
                    )}
                  </div>
                  <div className="col d-flex flex-column">
                    <p className="font-weight-bold text-dark mb-0">
                      {u.firstName} {u.lastName}
                    </p>
                    <small className="text-text-light-gray">{u.email}</small>
                  </div>
                  {state.dashboardUsers.length > 1 ? (
                    <div className="col-auto">
                      <button
                        className="btn btn-link text-decoration-none text-icon-link-gray p-0 shadow-none"
                        onClick={() => handleRemoveUser(u)}
                      >
                        <FontAwesomeIcon icon={faTimes} size="lg" />
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
        {state.error ? (
          <p className="text-danger three-quarter-text text-right mb-0">
            {state.error}
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-outline-secondary text-muted bg-white rounded-lg three-quarter-text py-0 px-3"
          disabled={state.requestPending}
          onClick={props.toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit rounded-lg three-quarter-text py-0 px-3"
          disabled={state.requestPending}
          onClick={handleSave}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Save'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .btn:focus {
          outline: none;
        }
      `}</style>
    </ModalLayout>
  );
}

AddDashboardUsersModal.propTypes = {
  dashboard: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.string),
  }),
  handleAddUsersSave: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};
